import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import OfficialSolutionsPanel from '../components/OfficialSolutionsPanel';
import TaskAnswerCheckPanel from '../components/TaskAnswerCheckPanel';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { runPython } from '../components/PyodideRunner';
import KonspektPythonCodeMirror from './KonspektPythonCodeMirror';
import { baseProgRole, ROLE_EXPLAIN, showsAnswerBlank } from '../utils/baseProgRole';
import { taskNavRef } from '../utils/taskNav';

const ExpandedTaskContext = createContext({
  expandedKey: null,
  setExpandedKey: () => {},
});

export function KonspektTaskExpandProvider({ children }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const value = useMemo(() => ({ expandedKey, setExpandedKey }), [expandedKey]);
  return <ExpandedTaskContext.Provider value={value}>{children}</ExpandedTaskContext.Provider>;
}

function previewText(statement, maxLen = 140) {
  const raw = String(statement || '')
    .replace(/<pre[\s\S]*?<\/pre>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen - 1)}…`;
}

function KonspektTaskNodeView({ node, updateAttributes, editor }) {
  const isAuthor = editor.isEditable;
  const reactId = useId();
  const taskRefAttr = String(node.attrs.taskRef || '').trim();
  const { expandedKey, setExpandedKey } = useContext(ExpandedTaskContext);
  const cardKey = `${reactId}:${taskRefAttr}`;
  const expanded = !isAuthor && expandedKey === cardKey;

  const [task, setTask] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('# Введите ваше решение здесь\n');
  const [stdin, setStdin] = useState('');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!taskRefAttr || isAuthor) {
      setTask(null);
      setLoadError('');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    api
      .get(`/tasks/${encodeURIComponent(taskRefAttr)}/`)
      .then((res) => {
        if (cancelled) return;
        setTask(res.data);
        setCode('# Введите ваше решение здесь\n');
      })
      .catch(() => {
        if (!cancelled) {
          setTask(null);
          setLoadError('Не удалось загрузить задачу');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [taskRefAttr, isAuthor]);

  useEffect(() => () => {
    abortRef.current?.abort?.();
  }, []);

  const toggleExpand = useCallback(() => {
    setExpandedKey((prev) => (prev === cardKey ? null : cardKey));
  }, [cardKey, setExpandedKey]);

  const handleRun = useCallback(async () => {
    abortRef.current?.abort?.();
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(true);
    setOutput('');
    try {
      const result = await runPython(code, stdin, {}, { signal: ac.signal });
      if (ac.signal.aborted) return;
      if (result?.success) {
        const text = String(result.output ?? '').trimEnd();
        setOutput(text.length ? text : '(нет вывода)');
      } else {
        const errText = String(result?.error || 'Ошибка').trim();
        const out = String(result?.output || '').trimEnd();
        setOutput(out ? `${out}\n\n${errText}` : errText);
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setOutput(String(e?.message || e || 'Ошибка запуска'));
    } finally {
      if (!ac.signal.aborted) setRunning(false);
      abortRef.current = null;
    }
  }, [code, stdin]);

  if (isAuthor) {
    return (
      <NodeViewWrapper className="konspekt-task-wrap konspekt-task-wrap--author" data-drag-handle="">
        <div className="konspekt-task-author-row">
          <span className="konspekt-task-author-label">Задача из банка</span>
          <input
            type="text"
            className="form-input konspekt-task-ref-input"
            value={taskRefAttr}
            placeholder="id или slug задачи"
            onChange={(e) => updateAttributes({ taskRef: e.target.value.trim() })}
          />
        </div>
      </NodeViewWrapper>
    );
  }

  if (!taskRefAttr) {
    return (
      <NodeViewWrapper className="konspekt-task-wrap">
        <p className="text-secondary">Не указана задача</p>
      </NodeViewWrapper>
    );
  }

  if (loading) {
    return (
      <NodeViewWrapper className="konspekt-task-wrap">
        <p className="text-secondary">Загрузка задачи…</p>
      </NodeViewWrapper>
    );
  }

  if (loadError || !task) {
    return (
      <NodeViewWrapper className="konspekt-task-wrap">
        <p className="text-secondary">{loadError || 'Задача не найдена'}</p>
      </NodeViewWrapper>
    );
  }

  const role = baseProgRole(task.expected_answer);
  if (role === ROLE_EXPLAIN) {
    return (
      <NodeViewWrapper className="konspekt-task-wrap">
        <div className="konspekt-task-explain">
          <MarkdownText className="statement-text" text={task.statement || ''} />
        </div>
      </NodeViewWrapper>
    );
  }

  const workRef = taskNavRef(task) || taskRefAttr;
  const title =
    task.title?.trim()
    || (task.source_number ? `№ ${task.source_number}` : `Задача ${workRef}`);
  const showBlank = showsAnswerBlank(task.expected_answer);

  return (
    <NodeViewWrapper className={`konspekt-task-wrap ${expanded ? 'is-expanded' : ''}`}>
      <div className="konspekt-task-card">
        <div className="konspekt-task-card-head">
          <div className="konspekt-task-card-title">
            <strong>{title}</strong>
            {task.source_name ? (
              <span className="text-secondary konspekt-task-meta">
                {task.source_name}
                {task.source_number ? ` · ${task.source_number}` : ''}
              </span>
            ) : null}
          </div>
          <div className="konspekt-task-card-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={toggleExpand}>
              {expanded ? 'Свернуть' : 'Решать'}
            </button>
            <Link className="btn btn-outline btn-sm" to={`/work/${workRef}`}>
              На весь экран
            </Link>
          </div>
        </div>
        {!expanded ? (
          <p className="konspekt-task-preview text-secondary">{previewText(task.statement)}</p>
        ) : (
          <div className="konspekt-task-body">
            <MarkdownText className="statement-text" text={task.statement || ''} />
            <StatementAttachmentLinks attachments={task.statement_attachments} variant="work" />
            <div className="konspekt-task-editor">
              <div className="konspekt-task-editor-head">
                <span>Черновик</span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={running}
                  onClick={() => void handleRun()}
                >
                  {running ? '…' : 'Запустить'}
                </button>
              </div>
              <KonspektPythonCodeMirror value={code} onChange={setCode} height="min(40vh, 260px)" />
              <label className="konspekt-py-stdin-label">
                Ввод (stdin)
                <textarea
                  className="konspekt-py-stdin"
                  rows={2}
                  value={stdin}
                  onChange={(e) => setStdin(e.target.value)}
                />
              </label>
              {output ? (
                <pre className="konspekt-py-output">{output}</pre>
              ) : null}
            </div>
            {showBlank ? (
              <TaskAnswerCheckPanel
                expectedAnswer={task.expected_answer}
                taskKey={workRef}
              />
            ) : (
              <p className="text-secondary konspekt-task-hint">
                Напишите программу и сравните с разбором, когда будете готовы.
              </p>
            )}
            <OfficialSolutionsPanel task={task} />
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}

export const KonspektTask = Node.create({
  name: 'konspektTask',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      taskRef: { default: '', rendered: false },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-type="konspekt-task"]',
        getAttrs: (el) => ({
          taskRef: el.getAttribute('data-task-ref') || '',
        }),
      },
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'konspekt-task',
        'data-task-ref': node.attrs.taskRef || '',
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(KonspektTaskNodeView);
  },
});
